import { Controller } from '@hotwired/stimulus';
import { AsyncSelectDraft } from '../lib/async_select_draft.js';

export default class extends Controller {
  static targets = ['input', 'option', 'empty', 'list', 'createOption', 'createCheckbox', 'createLabel', 'template',
    'selection', 'results', 'sentinel', 'more', 'loading', 'error'];
  static values = { createLabel: String, addedLabel: String, removedLabel: String,
    url: String, cursor: String, saved: Array, selected: Array, created: Array, fieldName: String, createFieldName: String };

  connect() {
    this.draft = new AsyncSelectDraft(this.savedValue, this.selectedValue, this.createdValue);
    this.pinned = new Map();
    this.rows = new Map(this.optionTargets.map(row => [row.dataset.asyncSelectKey, row]));
    this.rows.forEach((row, key) => {
      if (this.draft.atTop(key)) this.pinned.set(key, row);
    });
    this.query = '';
    this.cursor = this.cursorValue || null;
    this.generation = 0;
    this.exactMatch = false;
    this.render();
    this.syncSelection();
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) this.loadMore();
    }, { root: this.resultsTarget, rootMargin: '150px' });
    this.observer.observe(this.sentinelTarget);
  }

  snapshot() {
    return this.draft.snapshot();
  }

  disconnect() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.observer?.disconnect();
    this.generation += 1;
  }

  filter() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.generation += 1;
    this.query = this.inputTarget.value.trim();
    this.cursor = null;
    this.exactMatch = false;
    this.rows.clear();
    this.failed = false;
    this.busy = true;
    this.resultsTarget.scrollTop = 0;
    this.render();
    this.timer = setTimeout(() => this.fetchPage(true), 200);
  }

  loadMore() {
    if (!this.busy && !this.failed && this.cursor) this.fetchPage(false);
  }

  retry() {
    this.fetchPage(this.failedReset);
  }

  async fetchPage(reset) {
    const generation = this.generation;
    this.request = new AbortController();
    this.busy = true;
    this.failed = false;
    this.controls();
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('query', this.query);
    if (!reset && this.cursor) url.searchParams.set('cursor', this.cursor);
    try {
      const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not load options');
      const page = await response.json();
      if (generation !== this.generation || !this.element.isConnected) return;
      const fragment = document.createElement('template');
      fragment.innerHTML = page.html;
      fragment.content.querySelectorAll('[data-async-select-target="option"]').forEach(row => {
        const key = row.dataset.asyncSelectKey;
        this.rows.set(key, this.pinned.get(key) || row);
      });
      this.cursor = page.next_cursor;
      this.exactMatch = page.exact_match;
      this.busy = false;
      this.render();
      // Continue if the first page did not fill the scrollable area.
      if (this.sentinelTarget.getBoundingClientRect().top < this.resultsTarget.getBoundingClientRect().bottom + 150) this.loadMore();
    } catch (error) {
      if (generation !== this.generation || error.name === 'AbortError') return;
      this.busy = false;
      this.failed = true;
      this.failedReset = reset;
      this.controls();
    }
  }

  sort(event) {
    if (event) {
      const row = event.target.closest('[data-async-select-target="option"]');
      const key = row.dataset.asyncSelectKey;
      this.draft.set(key, event.target.checked);
      this.pinned.set(key, row);
      this.syncSelection();
    }
    this.render();
  }

  syncSelection() {
    const fragment = document.createDocumentFragment();
    const { values, creations } = this.draft.submission();
    [[this.fieldNameValue, values], [this.createFieldNameValue, creations]].forEach(([field, values]) => {
      if (!field) return;
      ['', ...values].forEach(value => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = field;
        input.value = value;
        fragment.append(input);
      });
    });
    this.selectionTarget.replaceChildren(fragment);
    this.dispatch('change', { detail: {
      ...this.draft.submission(), snapshot: this.snapshot(), baseline: this.draft.baseline(),
      options: values.map(value => ({ value, label: this.pinned.get(AsyncSelectDraft.key(value))?.dataset.asyncSelectLabel || value }))
    } });
  }

  render() {
    const focused = document.activeElement;
    const query = this.query.toLocaleLowerCase();
    const visible = new Map(this.rows);
    this.pinned.forEach((row, key) => {
      if (row.dataset.asyncSelectLabel.toLocaleLowerCase().includes(query)) visible.set(key, row);
    });
    const rows = [...visible.entries()].sort(([left, leftRow], [right, rightRow]) => {
      return Number(this.draft.atTop(right)) - Number(this.draft.atTop(left)) ||
        Number(leftRow.dataset.asyncSelectPriority || 0) - Number(rightRow.dataset.asyncSelectPriority || 0) ||
        leftRow.dataset.asyncSelectLabel.localeCompare(rightRow.dataset.asyncSelectLabel);
    });
    rows.forEach(([key, row]) => {
      const input = row.querySelector('input');
      input.removeAttribute('name');
      input.checked = this.draft.selected.has(key);
      const state = this.draft.state(key);
      row.classList.toggle('border-success', state === 'added');
      row.classList.toggle('border-danger', state === 'removed');
      const label = row.querySelector('[data-async-select-state]');
      label.textContent = state === 'added' ? this.addedLabelValue : (state === 'removed' ? this.removedLabelValue : '');
      label.hidden = !state;
      label.classList.toggle('text-success', state === 'added');
      label.classList.toggle('text-danger', state === 'removed');
    });
    this.listTarget.replaceChildren(...rows.map(([, row]) => row));
    if (this.element.contains(focused)) focused.focus({ preventScroll: true });
    this.controls();
  }

  searchEnter(event) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    if (event.isComposing || event.altKey || event.shiftKey) { event.stopImmediatePropagation(); return; }
    if (!this.query || this.busy || this.failed) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  toggleFirst(event) {
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    if (!this.query || this.busy || this.failed) return;
    const first = this.listTarget.firstElementChild?.querySelector('input');
    if (first) {
      first.click();
    } else if (this.hasCreateOptionTarget && !this.createOptionTarget.classList.contains('d-none')) {
      this.createCheckboxTarget.click();
    }
  }

  controls() {
    const query = this.query.toLocaleLowerCase();
    const localMatch = [...this.pinned.values()].some(row => row.dataset.asyncSelectLabel.toLocaleLowerCase() === query);
    const canCreate = this.hasCreateOptionTarget && this.query && !this.busy && !this.failed && !this.exactMatch && !localMatch;
    if (this.hasCreateOptionTarget) {
      this.createOptionTarget.classList.toggle('d-none', !canCreate);
      this.createCheckboxTarget.checked = false;
      this.createLabelTarget.textContent = this.createLabelValue.replace('%{name}', this.query);
    }
    this.loadingTarget.hidden = !this.busy;
    this.errorTarget.hidden = !this.failed;
    this.sentinelTarget.hidden = !this.cursor || this.failed;
    this.moreTarget.disabled = Boolean(this.busy);
    this.emptyTarget.hidden = this.listTarget.children.length > 0 || canCreate || this.busy || this.failed;
    this.resultsTarget.setAttribute('aria-busy', String(Boolean(this.busy)));
    this.element.querySelectorAll('[data-async-select-enter-hint]').forEach(hint => hint.classList.add('d-none'));
    if (this.query && !this.busy && !this.failed && document.activeElement === this.inputTarget) {
      const row = this.listTarget.firstElementChild || (canCreate ? this.createOptionTarget : null);
      row?.querySelector('[data-async-select-enter-hint]')?.classList.remove('d-none');
    }
  }

  create() {
    const label = this.query;
    if (!this.createCheckboxTarget.checked || !label || this.busy || this.failed || this.exactMatch) return;
    const row = this.templateTarget.content.firstElementChild.cloneNode(true);
    const key = AsyncSelectDraft.key(label, true);
    row.dataset.asyncSelectKey = key;
    row.dataset.asyncSelectLabel = label;
    row.querySelector('input').value = label;
    row.querySelector('[data-async-select-label-text]').textContent = label;
    this.draft.set(key, true);
    this.pinned.set(key, row);
    this.syncSelection();
    this.inputTarget.value = '';
    this.filter();
    row.querySelector('input').focus({ preventScroll: true });
  }
}
